import {
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select as MuiSelect,
  SelectChangeEvent,
} from '@mui/material';
import { getBusinessContacts } from 'lib/api/contacts';
import { Contact } from 'lib/api/contacts/types';
import { FieldDefinition, PortfolioTemplate } from 'lib/types/models';
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'services/redux/store';

import { CustomHandle } from '..';
import Avals from './Inputs/Avals';
import { Aval } from './Inputs/Avals/types';
import { DateInput } from './Inputs/DateInput';
import { MultiSelect } from './Inputs/MultiSelect';
import OperationAssociation from './Inputs/OperationAssociation';
import { OPERATION_TYPES } from './Inputs/OperationAssociation/operations';
import { SingleSelect } from './Inputs/SingleSelect';
import { TextInput } from './Inputs/TextInput';
import TemplateSelector from './TemplateSelector';
import { useCreateTemplate } from './useCreatePortfolio';

interface Props {
  templates: PortfolioTemplate[];
  businessIdentifier: string | undefined;
}

function FieldRenderer({
  fields,
  field,
  value,
  onChange,
  businessUsers,
  ownUsers,
  businessIdentifier,
}: {
  fields: FieldDefinition[];
  field: FieldDefinition;
  value: unknown;
  onChange: (value: unknown) => void;
  businessUsers: Contact[] | undefined;
  ownUsers: Contact[] | undefined;
  businessIdentifier: string | undefined;
}) {
  const findOptions = () => {
    switch (field.optionsType) {
      case 'business-users':
        return businessUsers?.map((user) => ({
          value: user.id.toString(),
          label: getUserLabel(user),
        }));
      case 'own-users':
        return ownUsers?.map((user) => ({
          value: user.id.toString(),
          label: getUserLabel(user),
        }));
      case 'operation-types':
        return Object.values(OPERATION_TYPES).map((operation) => ({
          value: operation.key,
          label: operation.label,
        }));
    }
  };

  const findOperationType = () => {
    return fields.find((f) => f.id === 'requestType')?.defaultValue as string;
  };

  const getUserLabel = (user: Contact) => {
    if (!user.name) return '';
    const nameParts = user.name.split(' ').filter(Boolean);
    if (nameParts.length === 0) return '';
    if (nameParts.length === 1) return nameParts[0];
    return `${nameParts[0]} ${nameParts[1]}`;
  };

  switch (field.type) {
    case 'text':
      return (
        <TextInput
          value={value as string}
          onChange={onChange}
          placeholder={field.placeholder}
          required={field.required}
          label={field.label}
          helperText={field.helperText}
          disabled={field.disabled}
        />
      );
    case 'date':
      return (
        <DateInput
          value={value as string}
          onChange={(value: string) => onChange(value)}
          label={field.label}
          placeholder={field.placeholder}
          required={field.required}
          helperText={field.helperText}
          disabled={field.disabled}
        />
      );
    case 'select':
      return (
        <SingleSelect
          options={findOptions() ?? []}
          value={value as string}
          onChange={onChange}
          label={field.label}
          placeholder={field.placeholder}
          helperText={field.helperText}
          disabled={field.disabled}
        />
      );
    case 'multiselect':
      return (
        <MultiSelect
          options={findOptions() ?? []}
          value={value as string[]}
          onChange={onChange}
          label={field.label}
          placeholder={field.placeholder}
          helperText={field.helperText}
          min={field.validations?.minLength}
          max={field.validations?.maxLength}
          disabled={field.disabled}
        />
      );
    case 'operation':
      return (
        <OperationAssociation
          value={value as string}
          type={findOperationType()}
          onChange={onChange}
          businessIdentifier={businessIdentifier}
        />
      );
    case 'aval':
      return (
        <Avals
          value={value as Aval[]}
          onChange={onChange}
          businessUsers={businessUsers}
        />
      );
    default:
      return null;
  }
}

const TemplateForm = forwardRef<CustomHandle, Props>(
  ({ templates, businessIdentifier }, ref) => {
    const dispatch = useDispatch();
    const user = useSelector((state: RootState) => state.auth.user);
    const ownIdentifier = user?.businessIdentifier;
    const query = new URLSearchParams({
      isLegalRepresentative: 'true',
    }).toString();
    const { data: { items: businessUsers } = { items: [] } } =
      getBusinessContacts(dispatch, businessIdentifier, query);
    const { data: { items: ownUsers } = { items: [] } } = getBusinessContacts(
      dispatch,
      ownIdentifier,
      query,
    );
    const [template, setTemplate] = useState<PortfolioTemplate | undefined>();
    const fields = template?.signDocumentTemplates[0].fieldMap ?? {};
    const orderedFields = Object.values(fields).sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0),
    );
    const [formValues, setFormValues] = useState<Record<string, unknown>>(() =>
      initValuesFromTemplate(fields),
    );
    const { handleCreateTemplate } = useCreateTemplate(
      template,
      businessIdentifier,
      formValues,
    );

    useImperativeHandle(ref, () => ({
      submit: handleCreateTemplate,
    }));

    function initValuesFromTemplate(fields: Record<string, FieldDefinition>) {
      const initial: Record<string, unknown> = {};
      for (const f in fields) {
        const field = fields[f];
        if (field.type === 'multiselect' || field.type === 'aval') {
          initial[f] = field.defaultValue ?? [];
        } else {
          initial[f] = field.defaultValue ?? '';
        }
      }
      return initial;
    }

    function handleChange(fieldId: string, value: unknown) {
      setFormValues((prev) => ({ ...prev, [fieldId]: value }));
    }

    function handleSetTemplate(event: SelectChangeEvent<string>) {
      const templateId = event.target.value;
      const selectedTemplate = templates.find((t) => t.id === templateId);
      setTemplate(selectedTemplate);
    }

    useEffect(() => {
      const initialValues = initValuesFromTemplate(fields);
      setFormValues(initialValues);
    }, [template]);

    return (
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <FormControl fullWidth margin="dense">
            <InputLabel id="template-label">
              {templates.length === 0 ? 'No existen plantillas' : 'Plantilla'}
            </InputLabel>
            <MuiSelect
              labelId="template-label"
              label="Plantilla"
              value={template?.id ?? ''}
              onChange={handleSetTemplate}
              disabled={templates.length === 0}
              error={templates.length === 0}
            >
              {templates.map((templateItem) => (
                <MenuItem key={templateItem.id} value={templateItem.id}>
                  {templateItem.description || 'Sin descripción'}
                </MenuItem>
              ))}
            </MuiSelect>
          </FormControl>
          <Grid container spacing={2}>
            {orderedFields.map((field) => (
              <Grid item xs={12} key={field.id}>
                <FieldRenderer
                  fields={Object.values(fields)}
                  field={field}
                  value={formValues[field.id]}
                  onChange={(value: unknown) => handleChange(field.id, value)}
                  businessUsers={businessUsers}
                  ownUsers={ownUsers}
                  businessIdentifier={businessIdentifier}
                />
              </Grid>
            ))}
          </Grid>
        </Grid>
        <Grid item xs={6}>
          <TemplateSelector template={template} />
        </Grid>
      </Grid>
    );
  },
);

TemplateForm.displayName = 'TemplateForm';
export default TemplateForm;
